import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  accionsApi,
  CANALS,
  MOTIUS_TANCAMENT,
  useAccioActivacio,
  useCandidatsEscalat,
  useCercaUsuaris,
  type ActivacioDto,
  type CandidatEscalatDto,
  type DestinatariDto,
  type MotiuTancament,
  type UsuariCercaDto,
} from '@/api/activacions';
import { useMissatgeError } from '@/api/errors';
import { useExpertsServeis } from '@/api/planificacio';
import type { Canal } from '@/api/types';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Avatar, Camp, Modal } from './comu';

const FORMATS = ['pdf', 'csv', 'xlsx'] as const;

interface PropsModal {
  activacio: ActivacioDto;
  onTanca: () => void;
}

function Peu({
  onTanca,
  onEnvia,
  pendent,
  deshabilitat,
  etiqueta,
}: {
  onTanca: () => void;
  onEnvia: () => void;
  pendent: boolean;
  deshabilitat?: boolean;
  etiqueta: string;
}) {
  const { t } = useTranslation('comu');
  return (
    <>
      <button type="button" className="btn btn--s" onClick={onTanca}>
        {t('accions.cancellar')}
      </button>
      <button
        type="button"
        className="btn btn--s btn--primary"
        disabled={pendent || deshabilitat}
        onClick={onEnvia}
      >
        {etiqueta}
      </button>
    </>
  );
}

function ErrorMutacio({ error }: { error: unknown }) {
  const missatge = useMissatgeError();
  if (!error) return null;
  return (
    <p className="error-text" role="alert">
      {missatge(error)}
    </p>
  );
}

export function ModalNota({ activacio, onTanca }: PropsModal) {
  const { t } = useTranslation('operacio');
  const [text, setText] = useState('');
  const m = useAccioActivacio(activacio.id, (s: string) => accionsApi.nota(activacio.id, s));
  return (
    <Modal
      titol={t('detall.modals.nota')}
      onTanca={onTanca}
      screen="W-04"
      peu={
        <Peu
          onTanca={onTanca}
          pendent={m.isPending}
          deshabilitat={!text.trim()}
          etiqueta={t('detall.accions.afegeixNota')}
          onEnvia={() => m.mutate(text.trim(), { onSuccess: onTanca })}
        />
      }
    >
      <Camp etiqueta={t('detall.camps.nota')}>
        <textarea
          className="input"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Camp>
      <ErrorMutacio error={m.error} />
    </Modal>
  );
}

function CandidatOpcio({
  c,
  seleccionat,
  onTria,
}: {
  c: CandidatEscalatDto;
  seleccionat: boolean;
  onTria: () => void;
}) {
  return (
    <li>
      <label className="check">
        <input type="radio" name="candidat-escalat" checked={seleccionat} onChange={onTria} />
        <span>
          <strong>{c.nom ?? `#${c.usuariId}`}</strong>
          {c.especialitat ? <span className="dim"> · {c.especialitat}</span> : null}
        </span>
      </label>
    </li>
  );
}

export function ModalEscalar({ activacio, onTanca }: PropsModal) {
  const { t } = useTranslation('operacio');
  const [motiu, setMotiu] = useState('');
  const [text, setText] = useState('');
  const [cerca, setCerca] = useState('');
  const [triat, setTriat] = useState<CandidatEscalatDto | null>(null);
  useEffect(() => {
    const h = setTimeout(() => setCerca(text), 300);
    return () => clearTimeout(h);
  }, [text]);
  const candidats = useCandidatsEscalat(activacio.id, cerca);
  const llista = candidats.data ?? [];
  const experts = llista.filter((c) => c.expert);
  const altres = llista.filter((c) => !c.expert);
  const m = useAccioActivacio(activacio.id, (usuariId: number | undefined) =>
    accionsApi.escalar(activacio.id, motiu.trim(), usuariId),
  );
  return (
    <Modal
      titol={t('detall.modals.escalar')}
      onTanca={onTanca}
      screen="W-04"
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('comu:accions.cancellar')}
          </button>
          <button
            type="button"
            className="btn btn--s"
            disabled={m.isPending}
            onClick={() => m.mutate(undefined, { onSuccess: onTanca })}
          >
            {t('detall.escalarCircuit')}
          </button>
          <button
            type="button"
            className="btn btn--s btn--primary"
            disabled={m.isPending || !triat}
            onClick={() => triat && m.mutate(triat.usuariId, { onSuccess: onTanca })}
          >
            {triat
              ? t('detall.escalarA', { nom: triat.nom ?? `#${triat.usuariId}` })
              : t('detall.accions.escala')}
          </button>
        </>
      }
    >
      <p className="dim">{t('detall.escalarTria')}</p>
      <input
        className="input"
        type="search"
        placeholder={t('detall.escalarCerca')}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {experts.length > 0 ? (
        <>
          <h4>{t('detall.escalarExperts')}</h4>
          <ul className="llista">
            {experts.map((c) => (
              <CandidatOpcio
                key={c.usuariId}
                c={c}
                seleccionat={triat?.usuariId === c.usuariId}
                onTria={() => setTriat(c)}
              />
            ))}
          </ul>
        </>
      ) : null}
      {altres.length > 0 ? (
        <>
          <h4>{t('detall.escalarAltres')}</h4>
          <ul className="llista" style={{ maxHeight: 220, overflowY: 'auto' }}>
            {altres.map((c) => (
              <CandidatOpcio
                key={c.usuariId}
                c={c}
                seleccionat={triat?.usuariId === c.usuariId}
                onTria={() => setTriat(c)}
              />
            ))}
          </ul>
        </>
      ) : null}
      {!candidats.isLoading && llista.length === 0 ? (
        <p className="dim">{t('detall.escalarSenseCandidats')}</p>
      ) : null}
      <ErrorMutacio error={candidats.error} />
      <p className="dim">{t('detall.escalarText', { nivell: activacio.nivellEscalat + 1 })}</p>
      <Camp etiqueta={t('detall.camps.motiu')}>
        <textarea
          className="input"
          rows={3}
          value={motiu}
          onChange={(e) => setMotiu(e.target.value)}
        />
      </Camp>
      <ErrorMutacio error={m.error} />
    </Modal>
  );
}

export function ModalItcau({ activacio, onTanca }: PropsModal) {
  const { t } = useTranslation('operacio');
  const [ref, setRef] = useState(activacio.itcau?.ref ?? '');
  const [url, setUrl] = useState(activacio.itcau?.url ?? '');
  const m = useAccioActivacio(activacio.id, () =>
    accionsApi.itcau(activacio.id, { ref: ref.trim() || null, url: url.trim() || null }),
  );
  return (
    <Modal
      titol={t('detall.modals.itcau')}
      onTanca={onTanca}
      screen="W-04"
      peu={
        <Peu
          onTanca={onTanca}
          pendent={m.isPending}
          deshabilitat={!ref.trim()}
          etiqueta={t('comu:accions.desar')}
          onEnvia={() => m.mutate(undefined, { onSuccess: onTanca })}
        />
      }
    >
      <p className="dim">{t('detall.itcauText')}</p>
      <Camp etiqueta={t('detall.camps.itcauRef')}>
        <input className="input" value={ref} required onChange={(e) => setRef(e.target.value)} />
      </Camp>
      <Camp etiqueta={t('detall.camps.itcauUrl')}>
        <input className="input" type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
      </Camp>
      <ErrorMutacio error={m.error} />
    </Modal>
  );
}

export function ModalExpert({ activacio, onTanca }: PropsModal) {
  const { t } = useTranslation('operacio');
  const serveiId = activacio.serveiIds[0];
  const [consulta] = useExpertsServeis(serveiId != null ? [serveiId] : []);
  const experts = [...(consulta?.data ?? [])].sort((x, y) =>
    x.tipus === y.tipus ? x.ordre - y.ordre : x.tipus === 'TITULAR' ? -1 : 1,
  );
  const [triat, setTriat] = useState<number | null>(null);
  const m = useAccioActivacio(activacio.id, () =>
    accionsApi.expert(activacio.id, triat ?? undefined),
  );
  return (
    <Modal
      titol={t('detall.modals.expert')}
      onTanca={onTanca}
      screen="W-04"
      icona={<Icona nom="person-check" className="icon-20 c-blue" />}
      peu={
        <Peu
          onTanca={onTanca}
          pendent={m.isPending}
          etiqueta={t('detall.accions.activaExpert')}
          onEnvia={() => m.mutate(undefined, { onSuccess: onTanca })}
        />
      }
    >
      <p className="dim" style={{ margin: 0 }}>
        {t('detall.expertText', { servei: activacio.servei ?? '—' })}
      </p>
      <div className="panel panel--pla" role="radiogroup" aria-label={t('detall.expertTria')}>
        <label className="list-row" style={{ cursor: 'pointer' }}>
          <input
            type="radio"
            name="expert"
            checked={triat == null}
            onChange={() => setTriat(null)}
          />
          <div className="list-row__text">
            <div className="list-row__label">{t('detall.expertAutomatic')}</div>
            <div className="list-row__sub">{t('detall.expertAutomaticText')}</div>
          </div>
        </label>
        {experts.map((e) => (
          <label
            className="list-row"
            key={`${e.tipus}-${e.usuariId}`}
            style={{ cursor: 'pointer' }}
          >
            <input
              type="radio"
              name="expert"
              checked={triat === e.usuariId}
              onChange={() => setTriat(e.usuariId)}
            />
            <Avatar nom={e.nom} />
            <div className="list-row__text">
              <div className="list-row__label">{e.nom ?? `#${e.usuariId}`}</div>
              <div className="list-row__sub">
                {[t(`detall.rolExpert.${e.tipus}`), e.especialitat].filter(Boolean).join(' · ')}
              </div>
            </div>
          </label>
        ))}
      </div>
      <ErrorMutacio error={m.error} />
    </Modal>
  );
}

function nomUsuari(u: UsuariCercaDto): string {
  return [u.nom, u.cognoms].filter(Boolean).join(' ');
}

/** RF-022: afegeix destinataris addicionals a una activació oberta. */
export function ModalDestinataris({
  activacio,
  destinataris,
  onTanca,
}: PropsModal & { destinataris: DestinatariDto[] }) {
  const { t } = useTranslation('operacio');
  const [text, setText] = useState('');
  const [cerca, setCerca] = useState('');
  const [triats, setTriats] = useState<UsuariCercaDto[]>([]);
  useEffect(() => {
    const h = setTimeout(() => setCerca(text), 250);
    return () => clearTimeout(h);
  }, [text]);
  const usuaris = useCercaUsuaris(cerca);
  const jaHiSon = new Set(destinataris.map((d) => d.usuariId));
  const resultats = (usuaris.data ?? []).filter(
    (u) => !jaHiSon.has(u.id) && !triats.some((x) => x.id === u.id),
  );
  const m = useAccioActivacio(activacio.id, () =>
    accionsApi.afegeixDestinataris(
      activacio.id,
      triats.map((u) => u.id),
    ),
  );
  return (
    <Modal
      titol={t('detall.modals.destinataris')}
      onTanca={onTanca}
      screen="W-04"
      icona={<Icona nom="person-2" className="icon-20 c-blue" />}
      peu={
        <Peu
          onTanca={onTanca}
          pendent={m.isPending}
          deshabilitat={triats.length === 0}
          etiqueta={t('detall.accions.afegeixN', { count: triats.length })}
          onEnvia={() => m.mutate(undefined, { onSuccess: onTanca })}
        />
      }
    >
      <p className="dim" style={{ margin: 0 }}>
        {t('detall.destinatarisText')}
      </p>
      <label className="search">
        <Icona nom="magnifier" />
        <input
          type="search"
          value={text}
          placeholder={t('detall.cercaPersona')}
          aria-label={t('detall.cercaPersona')}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      {triats.length > 0 ? (
        <div className="chips">
          {triats.map((u) => (
            <button
              key={u.id}
              type="button"
              className="chip is-active"
              aria-label={t('detall.treu', { nom: nomUsuari(u) })}
              onClick={() => setTriats((l) => l.filter((x) => x.id !== u.id))}
            >
              {nomUsuari(u)}
              <Icona nom="xmark" />
            </button>
          ))}
        </div>
      ) : null}
      {cerca.trim().length >= 2 ? (
        <div className="panel panel--pla" style={{ maxHeight: 260, overflowY: 'auto' }}>
          {resultats.length === 0 ? (
            <div className="empty">
              {usuaris.isFetching ? t('comu:sessio.carregant') : t('detall.senseResultats')}
            </div>
          ) : (
            resultats.map((u) => (
              <button
                key={u.id}
                type="button"
                className="list-row"
                onClick={() => setTriats((l) => [...l, u])}
              >
                <Avatar nom={nomUsuari(u)} />
                <div className="list-row__text">
                  <div className="list-row__label">{nomUsuari(u)}</div>
                  <div className="list-row__sub">
                    {[u.departament, u.email].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <Icona nom="plus" className="list-row__chev c-blue" />
              </button>
            ))
          )}
        </div>
      ) : null}
      <ErrorMutacio error={usuaris.error} />
      <ErrorMutacio error={m.error} />
    </Modal>
  );
}

export function ModalTancar({ activacio, onTanca }: PropsModal) {
  const { t } = useTranslation('operacio');
  const tc = useTranslation('comu').t;
  const [motiu, setMotiu] = useState<MotiuTancament>(
    activacio.areaTipus === 'CRISI' ? 'FINALITZADA' : 'RESOLTA',
  );
  const [comentari, setComentari] = useState('');
  const [missatge, setMissatge] = useState('');
  const [mesures, setMesures] = useState('');
  const [notificar, setNotificar] = useState(true);
  const [canals, setCanals] = useState<Canal[]>(activacio.canals.filter((c) => c !== 'TRUCADA'));
  const m = useAccioActivacio(activacio.id, () =>
    accionsApi.tancar(activacio.id, {
      motiu,
      comentari: comentari.trim() || undefined,
      missatgeFinalitzacio: missatge.trim() || undefined,
      mesuresPendents: mesures.trim() || undefined,
      notificar,
      canals: notificar ? canals : [],
    }),
  );
  const commuta = (c: Canal) =>
    setCanals((a) => (a.includes(c) ? a.filter((x) => x !== c) : [...a, c]));
  return (
    <Modal
      titol={t('detall.modals.tancar')}
      onTanca={onTanca}
      screen="W-04"
      icona={<Icona nom="checkmark-circle" className="icon-20 c-blue" />}
      peu={
        <Peu
          onTanca={onTanca}
          pendent={m.isPending}
          deshabilitat={!comentari.trim()}
          etiqueta={t('detall.accions.tanca')}
          onEnvia={() => m.mutate(undefined, { onSuccess: onTanca })}
        />
      }
    >
      <Camp etiqueta={t('detall.camps.motiuTancament')}>
        <Desplegable
          aria-label={t('detall.camps.motiuTancament')}
          valor={motiu}
          onCanvia={(v) => setMotiu(v as MotiuTancament)}
          opcions={MOTIUS_TANCAMENT.map((mt) => ({
            valor: mt as string,
            etiqueta: tc(`estatActivacio.${mt}`),
          }))}
        />
      </Camp>
      <Camp etiqueta={t('detall.camps.comentari')}>
        <textarea
          className="input"
          rows={3}
          value={comentari}
          onChange={(e) => setComentari(e.target.value)}
        />
      </Camp>
      <Camp etiqueta={t('detall.camps.missatgeFinal')}>
        <textarea
          className="input"
          rows={3}
          placeholder={t('detall.missatgeFinalPlaceholder')}
          value={missatge}
          onChange={(e) => setMissatge(e.target.value)}
        />
      </Camp>
      <Camp etiqueta={t('detall.camps.mesuresPendents')}>
        <input className="input" value={mesures} onChange={(e) => setMesures(e.target.value)} />
      </Camp>
      <label className="switch-row">
        <Icona nom="bell" className="c-blue" />
        {t('detall.camps.notificar')}
        <input
          type="checkbox"
          className="switch"
          checked={notificar}
          onChange={(e) => setNotificar(e.target.checked)}
        />
      </label>
      {notificar ? (
        <div className="chips">
          {CANALS.map((c) => (
            <button
              key={c}
              type="button"
              className="chip"
              aria-pressed={canals.includes(c)}
              onClick={() => commuta(c)}
            >
              {tc(`canal.${c}`)}
            </button>
          ))}
        </div>
      ) : null}
      <ErrorMutacio error={m.error} />
    </Modal>
  );
}

export function ModalInforme({ activacio, onTanca }: PropsModal) {
  const { t } = useTranslation('operacio');
  const [format, setFormat] = useState<'pdf' | 'csv' | 'xlsx'>('pdf');
  const [pendent, setPendent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const descarrega = async () => {
    setPendent(true);
    setError(null);
    try {
      await accionsApi.informe(activacio.id, activacio.codi, format);
      onTanca();
    } catch (e) {
      setError(e);
    } finally {
      setPendent(false);
    }
  };
  return (
    <Modal
      titol={t('detall.modals.informe')}
      onTanca={onTanca}
      screen="W-44"
      peu={
        <Peu
          onTanca={onTanca}
          pendent={pendent}
          etiqueta={t('detall.accions.descarrega')}
          onEnvia={() => void descarrega()}
        />
      }
    >
      <p className="dim">{t('detall.informeText')}</p>
      <div className="segmented" role="group" aria-label={t('detall.camps.format')}>
        {FORMATS.map((fmt) => (
          <button
            key={fmt}
            type="button"
            aria-pressed={format === fmt}
            onClick={() => setFormat(fmt)}
          >
            {fmt.toUpperCase()}
          </button>
        ))}
      </div>
      <ErrorMutacio error={error} />
    </Modal>
  );
}
